/**
 * SignatureCard - Displays a single signature with actions
 */

import type { SignaturePublic } from '@garden-platform/shared';
import type * as React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { useSignatureImageUrl } from '../../hooks';

export interface SignatureCardProps {
  signature: SignaturePublic;
  onEdit: (signature: SignaturePublic) => void;
  onDelete: (id: string) => void;
  isDeleting?: boolean;
}

export const SignatureCard: React.FC<SignatureCardProps> = ({
  signature,
  onEdit,
  onDelete,
  isDeleting = false,
}) => {
  const imageUrl = useSignatureImageUrl(signature.id);

  return (
    <Card className={!signature.isActive ? 'opacity-60' : undefined}>
      <CardHeader className="flex flex-row items-start justify-between pb-2">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <h4 className="font-semibold">{signature.name}</h4>
            {!signature.isActive && (
              <Badge variant="outline" className="text-xs">
                Inactive
              </Badge>
            )}
          </div>
          {signature.title && <p className="text-sm text-muted-foreground">{signature.title}</p>}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex h-24 items-center justify-center rounded-md border bg-white">
          <img
            src={imageUrl}
            alt={`Signature of ${signature.name}`}
            className="max-h-20 max-w-full object-contain"
          />
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => onEdit(signature)}>
            Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDelete(signature.id)}
            disabled={isDeleting}
            className="text-red-600 hover:text-red-700"
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

SignatureCard.displayName = 'SignatureCard';
